import { InlineConfig, ProjectViewModel } from '../../../../../config/configs';
import {
  assertNeverForViewMode,
  OfflineStudentViewModes,
  OnlineStudentViewModes,
  StudentViewMode,
} from '../../../../../config/constants';
import { dynamicEnv } from '../../../../../config/dynamic/dynamicEnv';
import {
  applicationPreInit,
  FailedInitResult,
  InitResult,
} from '../../../../../config/dynamic/init/prefetchServerConfiguration';
import { throwOnUnset } from '../../../../../global/utils/throwOnUnset';
import { NullClient } from '../../../../store/middleware/tracking/clients/nullClient/nullClient';
import {
  getTrackingData,
  notifyMobileAppTrackedDataChanged,
} from '../../../../store/middleware/tracking/clients/offlineClient/mobileAppIntegration';
import { OfflineClient } from '../../../../store/middleware/tracking/clients/offlineClient/offlineClient';
import { ScormTrackingClient } from '../../../../store/middleware/tracking/clients/scorm/scormTrackingClient';
import { ServerClient } from '../../../../store/middleware/tracking/clients/serverClient/serverClient';
import { TinCanTrackingClient } from '../../../../store/middleware/tracking/clients/tincanClient/TinCanTrackingClient';
import { xApiWrapperFactory } from '../../../../store/middleware/tracking/clients/tincanClient/xApiWrapper';
import { IPersistenceTrackingClient } from '../../../../store/middleware/tracking/clients/trackingClient';
import type { ApiClient } from '@ael/core-api';
import { createApiClient } from '@ael/core-api';
import { getProjectViewModelFromServer } from './studentStartup';
import { safeInitializationOfTrackingClient } from './trackingStartup';
import { useQuery } from '@tanstack/react-query';
import { provideToken } from 'src/libraries/TokenProvider';

export type PreInitResult = {
  apiClient: ApiClient;
  preInitData: InitResult | FailedInitResult;
  projectGuid: string;
};

export const useApplicationPreInitQuery = (viewMode: OnlineStudentViewModes) => {
  return useQuery({
    queryKey: ['InitialServerConfigStartup'],
    queryFn: async () => {
      const backendUrl = throwOnUnset(dynamicEnv('BACKEND_URL'), 'BACKEND_URL missing.');
      const inlineConfig: InlineConfig = {
        VIEW_MODE: viewMode,
        PROJECT_ID: dynamicEnv('PROJECT_ID') ?? null,
        PROJECT_GUID: throwOnUnset(dynamicEnv('PROJECT_GUID'), 'PROJECT_GUID missing.'),
        AUTH0_AUDIENCE: dynamicEnv('AUTH0_AUDIENCE') ?? null,
        AUTH0_CLIENT_ID: dynamicEnv('AUTH0_CLIENT_ID') ?? null,
        AUTH0_DOMAIN: dynamicEnv('AUTH0_DOMAIN') ?? null,
        AUTHENTICATION_TOKEN: dynamicEnv('AUTHENTICATION_TOKEN') ?? null,
      };

      const provideAuthToken = async () => await provideToken(backendUrl, inlineConfig);
      const apiClient: ApiClient = createApiClient(backendUrl, provideAuthToken);
      const preInitData = await applicationPreInit(apiClient, viewMode, inlineConfig.PROJECT_GUID);
      const result: PreInitResult = {
        apiClient,
        preInitData,
        projectGuid: inlineConfig.PROJECT_GUID,
      };
      return result;
    },
    gcTime: Infinity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    throwOnError: true,
  });
};

export const useOnlineStudentStartupQuery = (
  preInitResult: PreInitResult | undefined | null,
  viewMode: OnlineStudentViewModes
) => {
  return useQuery({
    queryKey: ['StudentStartup'],
    queryFn: async () => {
      if (preInitResult?.preInitData.isSuccess !== true) {
        return null;
      }

      const projectViewModel: ProjectViewModel = await getProjectViewModelFromServer(
        viewMode,
        preInitResult.apiClient,
        preInitResult.projectGuid
      );

      const client = _getOnlineTrackingClient(preInitResult.apiClient, preInitResult.projectGuid, viewMode);
      const trackingResult = await safeInitializationOfTrackingClient(client);

      return { projectViewModel, trackingResult };
    },
    enabled: preInitResult?.preInitData.isSuccess === true,
    gcTime: Infinity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    throwOnError: true,
  });
};

export const useOfflineStartupQuery = (
  viewMode: OfflineStudentViewModes,
  projectViewModel: ProjectViewModel,
  isPanomio: boolean
) => {
  return useQuery({
    queryKey: ['StudentStartup'],
    queryFn: async () => {
      const client = _getOfflineTrackingClient(viewMode, projectViewModel, isPanomio);
      const trackingResult = await safeInitializationOfTrackingClient(client);

      return { projectViewModel, trackingResult };
    },
    gcTime: Infinity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    throwOnError: true,
  });
};

function _getOnlineTrackingClient(
  apiClient: ApiClient,
  projectGuid: string,
  viewMode: OnlineStudentViewModes
): IPersistenceTrackingClient {
  switch (viewMode) {
    case StudentViewMode.SCORM:
      return new ScormTrackingClient();
    case StudentViewMode.Basic:
      return new ServerClient(apiClient, projectGuid);
    case StudentViewMode.Public:
    case StudentViewMode.Preview:
      return new NullClient();
    default:
      return assertNeverForViewMode(viewMode);
  }
}

const _getOfflineTrackingClient = (
  viewMode: OfflineStudentViewModes,
  projectViewModel: ProjectViewModel,
  isPanomio: boolean
): IPersistenceTrackingClient => {
  switch (viewMode) {
    case StudentViewMode.Offline:
      return new OfflineClient(getTrackingData(), notifyMobileAppTrackedDataChanged);
    case StudentViewMode.ExportedPackaged_NullClient:
      return new NullClient();
    case StudentViewMode.Offline_SCORM:
      return new ScormTrackingClient();
    case StudentViewMode.Offline_TinCan:
    case StudentViewMode.TinCan: {
      const xApiBaseUrl: string = isPanomio ? 'https://panomio.com' : 'https://v360e.com';
      const courseGuid = projectViewModel.additionalSettings.guid; // Identifies for tincan storage
      const xApi = xApiWrapperFactory(courseGuid, xApiBaseUrl);
      const courseData = { guid: courseGuid, name: projectViewModel.config.name };
      return new TinCanTrackingClient(courseData, xApi, xApiBaseUrl);
    }
    default:
      return assertNeverForViewMode(viewMode);
  }
};
